import Vue from 'vue'
import { getActivePinia, Pinia } from 'pinia'

/**
 * Per-page permission answers that a component can read with one reactive dependency.
 *
 * In Vue 2 a computed that reads another computed (a Pinia getter included) inherits every dependency of it. An
 * `isEditable` that asks "is this page locked?" by scanning the element list therefore subscribes to every element,
 * once per block and table cell: on a 200-block page that is ~500 dependencies per computed, which makes unmounting
 * the page (Dep.removeSub is indexOf + splice) and every lock change take seconds.
 *
 * Here each key is answered by one private watcher that does the scan, and the answer is copied into a small
 * observable only when it changes. Readers depend on that observable alone. The watchers are async, so a burst of
 * store writes in one tick costs one recompute per key, never one per write.
 */

type Entry = { holder: { value: boolean }; unwatch: () => void }

const caches = new WeakMap<Pinia, Map<string, Entry>>()
let watcherHost: Vue | undefined
// Bumped by a dispose: readers still mounted re-run and get a live entry instead of a stopped one
const epoch = Vue.observable({ n: 0 })

function entriesOfActivePinia(): Map<string, Entry> {
  const pinia = getActivePinia()
  if (!pinia) return new Map()
  let entries = caches.get(pinia)
  if (!entries) {
    entries = new Map()
    caches.set(pinia, entries)
  }
  return entries
}

/**
 * The current value of `compute` for `key`, tracked by the caller as one dependency. `compute` must give the same
 * answer for the same key; it runs inside a private watcher, so its own reactive reads never reach the caller.
 */
export function isolatedPagePermission(key: string, compute: () => boolean): boolean {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  epoch.n
  const pinia = getActivePinia()
  // No store (logout tearing the page down): answer once. A watcher here would be stored nowhere and never stopped.
  if (!pinia) return compute()
  const entries = entriesOfActivePinia()
  let entry = entries.get(key)
  if (!entry) {
    watcherHost ??= new Vue()
    const holder = Vue.observable({ value: false })
    // `immediate` fills the holder synchronously, so the first reader gets the real answer
    const unwatch = watcherHost.$watch(compute, (value: boolean) => (holder.value = value), { immediate: true })
    entry = { holder, unwatch }
    entries.set(key, entry)
  }
  return entry.holder.value
}

/** Stops every cached answer of the active Pinia (leaving a space). Readers still mounted recreate theirs. */
export function disposePagePermissions(): void {
  const entries = entriesOfActivePinia()
  if (!entries.size) return
  entries.forEach(entry => entry.unwatch())
  entries.clear()
  epoch.n++
}
